import React from 'react';
import { ChevronRight } from 'lucide-react';
import MaskedMobile from '../../common/MaskedMobile';
import Pagination from '../../common/Pagination';
import { MarketingOrdersTable, OrderColumn } from './MarketingOrdersTable';
import { FarmExecutiveRow } from './farmIncentivesView';
import { isBelowFirstSlab, monthDates, monthLabel } from './marketingOrdersView';
import { LoadRowOrders, ORDERS_PAGE_SIZE, useExpandedOrders } from './useExpandedOrders';
import ExecIncentiveStatusCell from './ExecIncentiveStatusCell';

const formatINR = (val?: number) => '₹' + Number(val || 0).toLocaleString('en-IN');

export const farmKey = (r: FarmExecutiveRow) => `${r.exec_mobile}:${r.month}`;

const HEADERS = ['S.No', 'Farm Executive', 'Month', 'Orders', 'Total Sales', 'Farm Slab',
  'Credited', 'Pending', 'Total Incentive', 'Status', 'Action'];
const CENTER_COLS = ['S.No', 'Orders', 'Total Sales', 'Credited', 'Pending', 'Total Incentive'];

interface Props {
  rows: FarmExecutiveRow[];
  /** Fetches one page of a row's orders when it is expanded. */
  loadOrders: LoadRowOrders<FarmExecutiveRow>;
  /** The drill-down's Farm Incentive / Farm Slab columns. */
  incentiveColumns: OrderColumn[];
  canApprove: (row: FarmExecutiveRow) => boolean;
  /** farmKey of the row whose bulk approval is in flight. */
  approvingKey: string | null;
  onApprove: (row: FarmExecutiveRow) => void;
  marketingStaff?: any[];
  farmStaff?: any[];
  adminMobile?: string;
  hasApproveAccess?: boolean;
}

/**
 * Farm-executive-wise, month-wise farm incentives for the Farm Orders tab —
 * the same table and flow as Marketing Orders' executive table: View opens
 * that executive-month's orders right beneath the row. Farm amounts are
 * progressive (each sale paid at the band it falls in), so credited + pending
 * is the month. Approval is for the whole executive-month (Approve all),
 * by a Marketing Manager only — never per order.
 */
const FarmExecutiveIncentivesCard: React.FC<Props> = ({
  rows, loadOrders, incentiveColumns, canApprove, approvingKey, onApprove,
  marketingStaff, farmStaff, adminMobile, hasApproveAccess = false,
}) => {
  const {
    expandedKey, toggle, page, setPage, orders, totalPages, loading, error,
  } = useExpandedOrders(rows, farmKey, loadOrders);

  if (!rows.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="text-sm font-bold text-slate-800">Farm Executive-wise Orders &amp; Farm Slabs</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-100">
              {HEADERS.filter(h => h !== 'Action' || hasApproveAccess).map((h) => (
                <th key={h} className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${CENTER_COLS.includes(h) ? 'text-center' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, index) => {
              const key = farmKey(r);
              const selected = expandedKey === key;
              const below = !r.slab || isBelowFirstSlab(r.slab);
              return (
                <React.Fragment key={key}>
                  <tr className={`transition-colors ${selected ? 'bg-emerald-50/50' : 'hover:bg-slate-50/70'}`}>
                    <td className="px-4 py-3 font-semibold text-slate-500 text-[13px] text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          className="p-0.5 hover:bg-slate-200 rounded transition-colors border-none bg-transparent cursor-pointer shrink-0"
                        >
                          <ChevronRight
                            size={14}
                            className={`text-slate-400 transition-transform duration-200 ${selected ? 'rotate-90' : ''}`}
                          />
                        </button>
                        <span>{index + 1}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 whitespace-nowrap">
                        <span className="font-bold text-slate-800 text-[13px]">{r.exec_name || r.exec_mobile}</span>
                        <MaskedMobile mobile={r.exec_mobile} className="text-slate-500 font-medium text-[12px]" />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-slate-700 font-semibold text-[13px]">{monthLabel(r.month)}</div>
                      <div className="text-[11px] text-slate-500">{monthDates(r.month)}</div>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="font-bold text-[13px] text-slate-800">{r.orders}</span>
                        {r.not_eligible_orders > 0 && (
                          <span className="text-[10px] text-slate-400">{r.not_eligible_orders} not eligible</span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors ${
                            selected
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {selected ? 'Close' : 'View'}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-800 font-bold text-[13px] whitespace-nowrap text-center">
                      {r.month_sales}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.slab ? (
                        <div className="flex flex-col gap-0.5">
                          <span className={`inline-flex self-start px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide ${
                            below ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                            {r.slab}
                          </span>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-emerald-700 font-bold text-[13px] whitespace-nowrap text-center">
                      {formatINR(r.credited_amount)}
                    </td>
                    <td className="px-4 py-3 text-amber-700 font-bold text-[13px] whitespace-nowrap text-center">
                      {formatINR(r.pending_amount)}
                    </td>
                    <td className="px-4 py-3 font-black text-slate-900 text-[13px] whitespace-nowrap text-center">
                      {formatINR(r.total_incentive)}
                    </td>
                    <td className="px-4 py-3">
                      <ExecIncentiveStatusCell
                        pendingOrders={r.pending_orders}
                        pendingAmount={r.pending_amount}
                        creditedOrders={r.credited_orders}
                        creditedAmount={r.credited_amount}
                      />
                    </td>
                    {hasApproveAccess && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {canApprove(r) ? (
                          <button
                            type="button"
                            onClick={() => onApprove(r)}
                            disabled={approvingKey === key}
                            className="px-3 py-1.5 rounded-xl bg-[#1B4332] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#143427] transition-all shadow-xs disabled:opacity-50 cursor-pointer"
                          >
                            {approvingKey === key ? 'Approving…' : 'Approve all'}
                          </button>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    )}
                  </tr>
                  {selected && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={hasApproveAccess ? HEADERS.length : HEADERS.length - 1} className="px-6 py-5">
                        <div className="space-y-3">
                          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                            Orders — {r.exec_name || r.exec_mobile} · {monthLabel(r.month)}
                          </p>
                          {error ? (
                            <p className="text-[12px] text-rose-600">{error}</p>
                          ) : (
                            <MarketingOrdersTable
                              orders={orders}
                              loading={loading}
                              page={page}
                              pageSize={ORDERS_PAGE_SIZE}
                              isOrgWide={false}
                              isMarketingManager={false}
                              marketingStaff={marketingStaff || []}
                              farmStaff={farmStaff || []}
                              adminMobile={adminMobile}
                              incentiveColumns={incentiveColumns}
                              emptyText="No visited orders for this executive and month."
                            />
                          )}
                          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FarmExecutiveIncentivesCard;
